import { gql } from '@/__generated__/gql';
import {
  BeginAllocationModalEntitiesForAllocationQuery,
  EntityForAllocation,
  FatInvestorsPageAllAdvisoryFirmListQuery
} from '@/__generated__/graphql';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Input from '@/components/basicComponents/input';
import { formatDollarInputValue, formatMaskedDollarInputValue } from '@/components/basicComponents/input/utils';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import RadioButton from '@/components/basicComponents/radioButton';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { ALLOCATOR_PAGE_CREATE_ALLOCATIONS_MUTATION } from '@/components/dealCatalog-page/allocator-page/queries';
import { CREATE_LEGAL_ENTITY_MUTATION } from '@/components/investors-page/editInvestorPage/queries';
import { ALL_ADVISORY_FIRM_LIST_QUERY, LIST_ENTITIES_FOR_ALLOCATION_QUERY } from '@/components/investors-page/queries';
import { SORT_ORDER } from '@/components/table/types';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { useCustomizationsData } from '@/hooks/useCustomizationsData';
import { themeVar } from '@/styles/theme';
import { useMutation, useQuery } from '@apollo/client';
import { TenantType } from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

interface IForm {
  advisoryFirm: string;
  investorOption: string | null;
  selectedInvestorEntity: string;
  newInvestorName: string;
  amount: string;
}

const defaultFormValues: IForm = {
  advisoryFirm: '',
  investorOption: null,
  selectedInvestorEntity: '',
  newInvestorName: '',
  amount: ''
};

const radioButtonOptions = ['Select an Existing Investor', 'Add a New Investor'];

const POTENTIAL_ALLOCATION_QUERY = gql(`
  query PendingAllocationPotentialAllocation($investmentId: String!, $legalEntityId: String!) {
    potentialAllocation(investmentId: $investmentId, legalEntityId: $legalEntityId) {
      committedCapital
    }
  }
`);

interface BeginAllocationProps {
  isOpen: boolean;
  minimum: number;
  investmentType: string;
  investmentId: string;
  onClose: () => void;
}

const BeginAllocation = ({ isOpen, minimum, investmentType, investmentId, onClose }: BeginAllocationProps) => {
  const { user } = useAuth();
  const params = useParams();
  const navigate = useNavigate();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { handleSubmit, setValue, watch, control } = useForm<IForm>({ defaultValues: defaultFormValues });

  const [listAdvisoryFirms, setListAdvisoryFirms] = useState<ISelectValue[]>([]);
  const [listEntities, setListEntities] = useState<ISelectValue[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const { data: customizationsData } = useCustomizationsData();

  const [createLegalEntity, { loading: createLegalEntityLoading }] = useMutation(CREATE_LEGAL_ENTITY_MUTATION);
  const [createAllocations, { loading: createAllocationsLoading }] = useMutation(ALLOCATOR_PAGE_CREATE_ALLOCATIONS_MUTATION);

  const [createdAllocations, setCreatedAllocations] = useState<any>(null);

  const { loading: potentialAllocationLoading, refetch: refetchPotentialAllocation } = useQuery(POTENTIAL_ALLOCATION_QUERY, {
    variables: {
      investmentId: params?.strategyId,
      legalEntityId: watch('selectedInvestorEntity')
    },
    skip: !watch('selectedInvestorEntity'),
    onCompleted: (data) => {
      if (data?.potentialAllocation) {
        setValue('amount', data.potentialAllocation.committedCapital.toString());
      }
    }
  });

  useEffect(() => {
    setValue('amount', minimum.toString());
  }, [minimum]);

  const { data: advisoryFirmListData, loading: advisoryFirmListLoading } = useQuery<FatInvestorsPageAllAdvisoryFirmListQuery>(
    ALL_ADVISORY_FIRM_LIST_QUERY,
    {
      skip: Boolean(user && user.tenant.type === TenantType.ADVISORY_FIRM),
      variables: {
        data: {
          sortBy: 'NAME',
          sortDirection: SORT_ORDER.ASC
        }
      },
      onCompleted: (data) => {
        setListAdvisoryFirms([...new Set(data.allAdvisoryFirmList.map((advisoryFirm) => ({ value: advisoryFirm.id, label: advisoryFirm.name })))]);
      }
    }
  );

  const { data: listEntitiesForAllocationData, loading: listEntitiesForAllocationLoading } = useQuery<BeginAllocationModalEntitiesForAllocationQuery>(
    LIST_ENTITIES_FOR_ALLOCATION_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      variables: {
        data: {
          investmentId: params?.strategyId,
          tenantName: !!watch('advisoryFirm')
            ? advisoryFirmListData?.allAdvisoryFirmList?.find((advisoryFirm) => advisoryFirm.id === watch('advisoryFirm'))?.name
            : null
        }
      },
      onCompleted: ({
        entitiesForAllocation
      }: {
        entitiesForAllocation: BeginAllocationModalEntitiesForAllocationQuery['entitiesForAllocation'];
      }) => {
        setListEntities(entitiesForAllocation.map((item: EntityForAllocation) => ({ value: item.id, label: item.entityName })));
      },
      onError: (error) => {
        console.error(error);
        if (listEntitiesForAllocationData) {
          setListEntities(
            listEntitiesForAllocationData.entitiesForAllocation.map((item: EntityForAllocation) => ({ value: item.id, label: item.entityName }))
          );
        }
      }
    }
  );

  useEffect(() => {
    if (customizationsData?.menuConfig?.transactions && createdAllocations && createdAllocations.length > 0) {
      navigate(`/transactions/generateSignaturePacket/${createdAllocations[0].id}`);
    }
  }, [createdAllocations]);

  const saveValue = async (data: IForm) => {
    setErrorMessage(null);
    const { advisoryFirm, investorOption, selectedInvestorEntity, newInvestorName, amount } = data;

    try {
      if (investorOption === radioButtonOptions[1]) {
        const { data } = await createLegalEntity({
          variables: {
            data: {
              entityName: newInvestorName,
              ...(!!advisoryFirm && { tenantId: advisoryFirm })
            }
          }
        });

        await createAllocations({
          variables: {
            data: [
              {
                investmentId: params?.strategyId,
                legalEntityId: data.createLegalEntity.id,
                committedCapital: parseFloat(amount),
                allocationStatus: 'Outstanding'
              }
            ]
          },
          onCompleted: (data) => {
            setCreatedAllocations(data.createAllocations);
          }
        });
        if (!customizationsData?.menuConfig?.transactions) {
          setShowSuccessDialog(true);
        }

        return;
      }
      await createAllocations({
        variables: {
          data: [
            {
              investmentId: params?.strategyId,
              legalEntityId: selectedInvestorEntity,
              committedCapital: parseFloat(amount),
              allocationStatus: 'Outstanding'
            }
          ]
        },
        onCompleted: (data) => {
          setCreatedAllocations(data.createAllocations);
        }
      });
      if (!customizationsData?.menuConfig?.transactions) {
        setShowSuccessDialog(true);
      }
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    }
  };

  const shouldShowAmountField = () => {
    const investorOptionValue = watch('investorOption');
    const selectedInvestorValue = watch('selectedInvestorEntity');

    return (investorOptionValue === radioButtonOptions[0] && !!selectedInvestorValue) || investorOptionValue === radioButtonOptions[1];
  };

  const shouldSelectInvestorField = () => {
    const advisoryFirmValue = watch('advisoryFirm');
    const investorOptionValue = watch('investorOption');

    return (
      (user && user.tenant.type === TenantType.PLATFORM_MANAGER && !!advisoryFirmValue && investorOptionValue === radioButtonOptions[0]) ||
      (user && user.tenant.type === TenantType.ADVISORY_FIRM && investorOptionValue === radioButtonOptions[0])
    );
  };

  if (showSuccessDialog) {
    return (
      <ModalWrappedContent
        isOpen={isOpen}
        onClose={onClose}
        cancelButtonName="Later"
        confirmButton={{
          name: 'Go to Pending Commitments',
          onClick: () => navigate(`/pendingCommitments`)
        }}
      >
        <SuccessModalBody>
          <ApprovedIcon width={60} height={60} />
          <ModalTitle>Success</ModalTitle>
          <ModalSubTitle>
            Allocation can be monitored on the <span style={{ fontWeight: 700 }}>Pending Commitments</span> Page.
          </ModalSubTitle>
        </SuccessModalBody>
      </ModalWrappedContent>
    );
  }

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      confirmButton={{
        name: 'Confirm',
        onClick: handleSubmit(saveValue),
        disabled: createLegalEntityLoading || createAllocationsLoading
      }}
      showRequiredFields={!createLegalEntityLoading && !createAllocationsLoading}
      showCloseIcon={!createLegalEntityLoading && !createAllocationsLoading}
      showCancelButton={!createLegalEntityLoading && !createAllocationsLoading}
      isUseOutsideClick={!createLegalEntityLoading && !createAllocationsLoading}
    >
      <ModalBody>
        <ModalTitle>Begin Allocation</ModalTitle>
        {createLegalEntityLoading || createAllocationsLoading ? (
          <ModalLoader />
        ) : (
          <>
            <ModalSubTitle>Select an Existing Investor or Add a New Investor.</ModalSubTitle>
            {errorMessage && (
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            )}
            {user && user.tenant.type === TenantType.PLATFORM_MANAGER && (
              <FieldWrapper>
                <Controller
                  name="advisoryFirm"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: 'Advisory Firm is required'
                    }
                  }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Select
                      label="Advisory Firm"
                      value={value || ''}
                      options={listAdvisoryFirms}
                      setValue={(newSelectedValue) => {
                        setValue('selectedInvestorEntity', '');
                        onChange(newSelectedValue);
                      }}
                      errorText={error?.message}
                      isRequiredField
                      loading={advisoryFirmListLoading}
                      size="lg"
                    />
                  )}
                />
              </FieldWrapper>
            )}
            <FieldWrapper>
              <Controller
                name="investorOption"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Required'
                  }
                }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <>
                    <RadioButtonsWrapper>
                      {radioButtonOptions.map((option, index) => (
                        <RadioButton
                          key={option + index}
                          name="investorOption"
                          radioButtonText={option}
                          selectedOption={value}
                          handleRadioChange={(event) => onChange(event.target.value)}
                        />
                      ))}
                    </RadioButtonsWrapper>
                    {error?.message && <ErrorText>{error?.message}</ErrorText>}
                  </>
                )}
              />
            </FieldWrapper>
            {shouldSelectInvestorField() && (
              <FieldWrapper>
                <Controller
                  name="selectedInvestorEntity"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: 'Investor is required'
                    }
                  }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Select
                      label="Select Investor"
                      value={value || ''}
                      options={listEntities}
                      setValue={(selectedEntityId) => {
                        onChange(selectedEntityId);
                      }}
                      errorText={error?.message}
                      isRequiredField
                      withSearch
                      loading={listEntitiesForAllocationLoading}
                      size="lg"
                    />
                  )}
                />
              </FieldWrapper>
            )}
            {watch('investorOption') === radioButtonOptions[1] && (
              <FieldWrapper>
                <Controller
                  name="newInvestorName"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: 'Investor Name is required'
                    }
                  }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input
                      label="Investor Entity Name"
                      value={value}
                      onChange={(e) => {
                        onChange(e.target.value);
                      }}
                      errorText={error?.message}
                      size="lg"
                      isRequiredField
                      autoFocus
                    />
                  )}
                />
              </FieldWrapper>
            )}
            {shouldShowAmountField() && (
              <FieldWrapper>
                <Controller
                  name="amount"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: 'Amount is required'
                    },
                    min: {
                      value: 1,
                      message: 'Amount must be greater than 0'
                    },
                    validate: (value) => {
                      if (parseFloat(value) < minimum) {
                        return 'Cannot be less than fund minimum';
                      }
                    }
                  }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <Input
                        value={value ? formatMaskedDollarInputValue(value) : ''}
                        onChange={(e) => {
                          const formattedValue = formatDollarInputValue(e.target.value);
                          onChange(formattedValue);
                        }}
                        label="Amount"
                        errorText={error?.message}
                        isRequiredField
                        withDollarSign
                        loading={potentialAllocationLoading}
                      />
                      <Description>Minimum: ${formatMaskedDollarInputValue(minimum.toString())}</Description>
                    </>
                  )}
                />
              </FieldWrapper>
            )}
          </>
        )}
      </ModalBody>
    </ModalWrappedContent>
  );
};

export default BeginAllocation;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SuccessModalBody = styled(ModalBody)`
  align-items: center;
`;

const ModalTitle = styled.h2`
  text-align: center;
  font-size: 33px;
  font-weight: 400;
  line-height: 46.2px;
  color: ${themeVar('font.strong')};
`;

const ModalSubTitle = styled.p`
  text-align: center;
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
`;

const FieldWrapper = styled.div`
  padding-top: 15px;
`;

const RadioButtonsWrapper = styled.div`
  display: flex;
`;

const Description = styled.span`
  padding-top: 10px;
  font-weight: 300;
  font-size: 13px;
  line-height: 18px;
  color: ${themeVar('font.weak')};
`;

const ErrorText = styled.p`
  color: ${themeVar('context.error')};
  font-family: Blinker;
  font-style: normal;
  font-weight: 400;
  font-size: 19px;
  line-height: 26.6px;
`;
